import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type SelectionPanelProps } from '../SelectionPanel/SelectionPanel.types';
import { type SelectionOption } from '../types/selection-option.types';
import { useSelectionAnnouncer } from '../useSelectionAnnouncer/useSelectionAnnouncer';
import { useSelectionFilter } from '../useSelectionFilter/useSelectionFilter';
import { useSelectionKeyboard } from '../useSelectionKeyboard/useSelectionKeyboard';

import { type ComboboxProps } from './Combobox.types';

export type UseComboboxLogicInput = Omit<
  ComboboxProps,
  'id' | 'className' | 'size' | 'placeholder' | 'aria-describedby' | 'aria-labelledby'
>;

export interface UseComboboxLogic {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  panel: Omit<SelectionPanelProps, 'mode'>;
  /** What the trigger shows: the option's label, or the raw off-list value. */
  summary: string;
  isClearVisible: boolean;
  clear: () => void;
}

const NOTHING: ReadonlySet<string> = new Set<string>();

/**
 * One value out of a **local** list that filters, and optionally lets the
 * operator name a value the list does not carry.
 *
 * The sibling of `ServerSelect` (same panel, same radio rows, same single-choice
 * gesture) for the case where the options are code-declared rather than fetched,
 * and of `MultiSelect` for the create affordance — neither of which could serve
 * this alone: `MultiSelect` speaks in chips and `string[]`, and `ServerSelect`
 * would need a fabricated query hook to stand over a constant.
 */
export function useComboboxLogic({
  value,
  onValueChange,
  options,
  groups,
  filterFn,
  isCreatable = false,
  validateCreate,
  onCreate,
  isDisabled = false,
  isReadOnly = false,
  isLoading = false,
  isError = false,
  onRetry,
  isClearable = true,
  searchPlaceholder,
  emptyLabel,
  emptyContent,
  loadingContent,
  errorContent,
  renderOption,
}: UseComboboxLogicInput): UseComboboxLogic {
  const { t } = useTranslation('uiKit');
  const [isOpen, setIsOpen] = useState(false);
  const [term, setTerm] = useState('');
  const announcer = useSelectionAnnouncer();

  const selected = useMemo(() => (value === null ? NOTHING : new Set([value])), [value]);

  const filtered = useSelectionFilter({
    options,
    groups,
    term,
    selected,
    filterFn,
    isCreatable: isCreatable && !isReadOnly,
    validateCreate,
    duplicateMessage: t('multiSelect.createDuplicate', { value: term.trim() }),
  });

  // Entry only: Radix's RadioGroup already moves between the rows in single mode.
  const keyboard = useSelectionKeyboard({ options: filtered.visibleOptions, isEntryOnly: true });

  const choose = useCallback(
    (candidate: string) => {
      onValueChange(candidate);
      // A single choice is the whole gesture — staying open would leave the
      // operator to dismiss a list they are done with.
      setIsOpen(false);
      setTerm('');
    },
    [onValueChange],
  );

  const onSelect = useCallback(
    (candidate: string) => {
      // Announced, not merely refused — silence reads as a broken control.
      if (isReadOnly) {
        announcer.announceReadOnlyRefusal();
        return;
      }

      choose(candidate);
    },
    [announcer, choose, isReadOnly],
  );

  const onCreateCandidate = useCallback(() => {
    const candidate = filtered.createCandidate;
    if (candidate === null || candidate.status !== 'ok') return;

    announcer.say(t('multiSelect.created', { value: candidate.value }));

    if (onCreate === undefined) {
      choose(candidate.value);
      return;
    }

    // The caller registers a real option; it decides what the value becomes, so
    // the panel only closes.
    onCreate(candidate.value);
    setIsOpen(false);
    setTerm('');
  }, [announcer, choose, filtered.createCandidate, onCreate, t]);

  const onOpenChange = useCallback((open: boolean) => {
    setIsOpen(open);
    // Reopening mid-search would show a list the operator did not ask for.
    if (!open) setTerm('');
  }, []);

  const isOptionDisabled = useCallback((option: SelectionOption) => option.isDisabled === true, []);

  const byValue = useMemo(
    () => new Map(options.map((option) => [option.value, option])),
    [options],
  );

  const panel: Omit<SelectionPanelProps, 'mode'> = {
    filter: { term, onChange: setTerm, placeholder: searchPlaceholder },
    list: {
      sections: filtered.sections,
      selected,
      // A chosen value on no option list still renders as a row — which is what
      // makes a shrinking catalogue survivable: the operator sees what is
      // configured instead of an empty control over a value that is really there.
      createdValues: filtered.createdValues,
      activeValue: null,
      isOptionDisabled,
      isReadOnly,
      onSelect,
      renderOption,
    },
    create:
      isCreatable && !isReadOnly
        ? {
            candidate: filtered.createCandidate,
            onCreate: onCreateCandidate,
            // Single choice has no ceiling to hit — that is the multi controls'
            // `maxSelected`, and one value is always replaceable.
            isDisabled: false,
          }
        : undefined,
    status: {
      isLoading,
      isError,
      onRetry,
      hiddenCount: filtered.hiddenCount,
      isEmpty: filtered.isEmpty,
      emptyLabel,
      emptyContent,
      loadingContent,
      errorContent,
    },
    announcement: announcer.announcement,
    singleValue: value,
    listRef: keyboard.listRef,
    onKeyDown: keyboard.onKeyDown,
  };

  return {
    isOpen,
    onOpenChange,
    panel,
    // The raw value when it is on no list: the trigger's job is to say what is
    // configured, and an empty trigger over a stored value is a lie.
    summary: value === null ? '' : (byValue.get(value)?.label ?? value),
    isClearVisible: isClearable && value !== null && !isDisabled && !isReadOnly,
    clear: useCallback(() => {
      onValueChange(null);
    }, [onValueChange]),
  };
}
